import { AppSettings } from "sh-application";
import { authentication } from "@microsoft/teams-js";
import { AxiosResponse } from "axios";
import { BEARERKEY, DEV_CLIENT_ID, GRAPH_RESOURCE_URI, PROD_CLIENT_ID, SHIFTS_RESOURCE_URI_PROD, TEAMS_SPACES_RESOURCE_URI, TOKEN_RESOURCES, WWW_AUTHENTICATE_HEADER } from "../../StaffHubConstants";
import { trace } from "owa-trace";
import * as msal from "@azure/msal-browser";
import { UserStore } from "sh-stores";
import { nestedAppAuth } from "@microsoft/teams-js";
import { ECSConfigKey, ECSConfigService, InstrumentationService } from "sh-services";
import { getGenericEventPropertiesObject, InstrumentationEventPropertyInterface } from "sh-instrumentation";
import { HostContextExpectedFailureReason } from "../../sh-application/hostsContainers/HostContextExpectedFailureReason";
type ReportExpectedErrorFn = (error: Error, reason: HostContextExpectedFailureReason) => void;

class AuthService {
    private _cachedToken: Map<string, string> = new Map<string, string>();
    private _pca: msal.IPublicClientApplication;
    private _hasInstrumentedGetAuthTokenComplete = false;
    private _isAuthTokenCoverageLogged = false;
    private _reportExpectedErrorFn: ReportExpectedErrorFn | null = null;

    /**
     * Sets the reportExpectedError function.
     * @param reportExpectedErrorFn The function to report expected errors.
     */
    public setReportExpectedErrorFunction(reportExpectedErrorFn: ReportExpectedErrorFn): void {
        this._reportExpectedErrorFn = reportExpectedErrorFn;
    }

    /**
     * Reports an expected exception using the IHostContainer.
     * We generally treat this as a permissions error because the user cannot be authenticated.
     * The Teams host app will give us more time to initialize since interactive auth may be required.
     * @param error The expected error.
     */
    public reportExpectedErrorToHostApp(error: Error): void {
        // Default to permission error
        let reason: HostContextExpectedFailureReason = HostContextExpectedFailureReason.PermissionError;

        // Check if the user is offline
        if (error.message && (error.message.includes("no_network_connectivity") || error.message.includes("NoNetwork"))) {
            reason = HostContextExpectedFailureReason.Offline;
        }

        if (this._reportExpectedErrorFn) {
            this._reportExpectedErrorFn(error, reason);
        }
    }

    /**
     * Return cached BEARER token for the Shifts Service
     */
    public getCachedShiftsBearerToken(): string {
        const cachedShiftsToken = this.getCachedToken(TOKEN_RESOURCES.ShiftsService);
        return cachedShiftsToken ? BEARERKEY + cachedShiftsToken : undefined;
    }

    /**
     * Return cached token
     */
    public getCachedToken(resource: string): string {
        return this._cachedToken.get(resource);
    }

    /**
     * Get the auth token for the session
     * @param {string} resource - used to fetch AAD with different resource URL.
     * @param {string} claims - optional claims to pass to AAD when requesting the token (Sent when handling a CAE authentication challenge)
     * @param {boolean} forceInteractiveAuth - whether to force interactive auth
     * @returns {Object} promise.
     */
    public async getToken(resource: string, claims: string = "", forceInteractiveAuth: boolean = false): Promise<string> {
        const isNaaEnabled = nestedAppAuth.isNAAChannelRecommended() && ECSConfigService.isECSFeatureEnabled(ECSConfigKey.NaaAuthEnabled);
        const startTime = Date.now();
        let token = undefined;
        try {
           token = isNaaEnabled ? await this.getTokenNAA(resource, claims, forceInteractiveAuth) : await this.getTokenTeams(resource, claims);
        }
        finally {
           this.logAuthTokenCoverageEvent(isNaaEnabled, !!token, startTime);
        }
        return token;
    }

    /**
     * Get the auth token for the session using the Teams SDK, legacy approach
     * @param {string} resource - used to fetch AAD with different resource URL.
     * @param {string} claims - optional claims to pass to AAD when requesting the token (Sent when handling a CAE authentication challenge)
     * @returns {Object} promise.
     */
    public getTokenTeams(resource: string, claims: string = ""): Promise<string> {
        const promise = new Promise<string>((resolve, reject) => {
            // For Teams mode, we request the user's auth token from the Teams app via the Teams SDK
            const authTokenRequest: authentication.AuthTokenRequest = {
                successCallback: async (token: string) => {
                    this._cachedToken.set(resource, token);
                    resolve(token);
                },
                failureCallback: (error: any) => {
                    trace.warn("Error received when getting token from Teams Client SDK: " + (error ? JSON.stringify(error) : ""));
                    reject(new Error(error));
                },
                resources: [ AppSettings.getSetting(resource)  ],
                ...(claims ? { claims: [claims] } : {})
            };
            authentication.getAuthToken(authTokenRequest);
        });
        return promise;
    }

    /**
     * Let's keep both Auth mechanisms in place, use ECS to turn that on/off.
     * We'll also need a check if the platform supports NAA before using it, and fall back to the old version otherwise (e.g. in Teams 1.x).
     * Get the auth token for the session using MSAL js, new NAA approach
     * @param resource used to fetch AAD with different resource URL.
     * @param claims optional claims to pass to AAD when requesting the token (Sent when handling a CAE authentication challenge)
     * @param forceInteractiveAuth whether to force interactive auth
     * @returns promise.
     */
    public async getTokenNAA(resource: string, claims: string = "", forceInteractiveAuth: boolean = false): Promise<string> {
        const { clientId, resourceUri } = this.getClientIdAndResourceUri(resource);

        const startInitTime = Date.now();
        if (!this._pca) {
            await this.initializePublicClient(clientId);
        }
        const initDuration = Date.now() - startInitTime;

        const startAccountTime = Date.now();
        const activeAccount = await this.getActiveAccount();
        const accountDuration = Date.now() - startAccountTime;

        const scope = resource === TOKEN_RESOURCES.GraphServiceResourceUri
            ? ["https://graph.microsoft.com/.default"]
            : [`${resourceUri}/.default`];

        const tokenRequest = {
            scopes: scope,
            account: activeAccount || undefined,
            claims: claims
        };

        let silentDuration = 0;
        let popupDuration = 0;
        let teamsAuthDuration = 0;
        let interactiveAuthRequired = forceInteractiveAuth;

        // Track the previous error type so we can let the NAA team know if NAA auth failed but the fallback to Teams auth succeeded
        let previousAuthErrorType: msal.AuthError = null;

        // Skip the silent token request if we are forcing interactive auth
        if (!forceInteractiveAuth) {
            try {
                const startSilentTime = Date.now();
                const result = await this._pca.acquireTokenSilent(tokenRequest);
                silentDuration = Date.now() - startSilentTime;
                this.logAuthTokenCompleteEvent(InstrumentationService.values.NaaSilentTokenSuccess, initDuration, accountDuration, silentDuration, popupDuration, teamsAuthDuration, startInitTime);
                const token = result.accessToken;
                this._cachedToken.set(resource, token);
                return token;
            } catch (error) {
                if (error instanceof msal.AuthError) {
                    previousAuthErrorType = error;

                    // If the error is an interaction required error, we need to fall back to the popup flow
                    if (error instanceof msal.InteractionRequiredAuthError) {
                        interactiveAuthRequired = true;

                        if (error.claims) {
                            // If the error has claims, we need to pass them to the popup flow
                            tokenRequest.claims = error.claims;
                        }
                    }

                    this.logMsalAuthError(error, "NAA_Failed", "getTokenNAA.SilentAuth", [getGenericEventPropertiesObject(InstrumentationService.properties.IsInteractiveAuthRequired, interactiveAuthRequired)]);
                }

                // Report this as an expected error to the host app
                this.reportExpectedErrorToHostApp(error);
            }
        }

        if (interactiveAuthRequired) {
            try {
                const startPopupTime = Date.now();
                const result = await this._pca.acquireTokenPopup(tokenRequest);
                popupDuration = Date.now() - startPopupTime;
                this.logAuthTokenCompleteEvent(InstrumentationService.values.NaaPopupTokenSuccess, initDuration, accountDuration, silentDuration, popupDuration, teamsAuthDuration, startInitTime, previousAuthErrorType);
                const token = result.accessToken;
                this._cachedToken.set(resource, token);
                return token;
            } catch (error) {
                previousAuthErrorType = error;
                this.logMsalAuthError(error, "NAA_Failed", "getTokenNAA.PopupAuth");
                // Report this as an expected error to the host app
                this.reportExpectedErrorToHostApp(error);
            }
        }

        try {
            const startTeamsTime = Date.now();
            const token = await this.getTokenTeams(resource, claims);
            teamsAuthDuration = Date.now() - startTeamsTime;
            this.logAuthTokenCompleteEvent(InstrumentationService.values.NaaFallbackToTeamsAuthSuccess, initDuration, accountDuration, silentDuration, popupDuration, teamsAuthDuration, startInitTime, previousAuthErrorType);
            return token;
        } catch (error) {
            InstrumentationService.trackException(error, `NAA_Failed`, "getTokenNAA.LegacyFTATeamsAuth");
            // Report this as an expected error to the host app
            this.reportExpectedErrorToHostApp(error);
            this.logAuthTokenCompleteEvent(InstrumentationService.values.NaaAllFailed, initDuration, accountDuration, silentDuration, popupDuration, teamsAuthDuration, startInitTime);
            throw error;
        }
    }

    /**
     * Log the NAA error.
     * @param error The error to log.
     * @param errorCategory The category of the error.
     * @param handledAt The location where the error was handled.
     * @param eventDataArray Additional event data to log.
     */
    private logMsalAuthError(error: msal.AuthError, errorCategory: string, handledAt: string, eventDataArray?: InstrumentationEventPropertyInterface[]): void {
        eventDataArray = eventDataArray || [];
        eventDataArray.push(getGenericEventPropertiesObject(InstrumentationService.properties.ErrorCode, error.errorCode));
        eventDataArray.push(getGenericEventPropertiesObject(InstrumentationService.properties.ErrorSubError, error.subError));
        InstrumentationService.trackException(error, errorCategory, handledAt, eventDataArray);
    }

    /**
     * Check if the error is an interaction required error.
     * @param error The error to check.
     * @returns True if the error is an interaction required error, false otherwise.
     */
    public IsInteractiveAuthRequiredError(error: Error): boolean {
        return error instanceof msal.InteractionRequiredAuthError;
    }

    /**
     * Log the auth token event (this is only logged once the first time this is called, so we don't flood the Instrumentation db).
     * @param authStage The stage of the authentication process.
     * @param initDuration The duration of the initialization process.
     * @param accountDuration The duration of the account retrieval process.
     * @param silentDuration The duration of the silent token request process.
     * @param popupDuration The duration of the popup token request process.
     * @param teamsAuthDuration The duration of the Teams authentication process.
     * @param startInitTime The start time of the initialization process.
     * @param previousErrorType The previous error type, if any.
     **/
    private logAuthTokenCompleteEvent(authStage: string, initDuration: number, accountDuration: number, silentDuration: number, popupDuration: number, teamsAuthDuration: number, startInitTime: number, previousErrorType: msal.AuthError = null): void {
        if (this._hasInstrumentedGetAuthTokenComplete) {
            return;
        }
        this._hasInstrumentedGetAuthTokenComplete = true;
        InstrumentationService.logPerfEvent(InstrumentationService.perfScenarios.GetAuthTokenComplete, [
            getGenericEventPropertiesObject(InstrumentationService.properties.IsNestedAppAuth, true),
            getGenericEventPropertiesObject(InstrumentationService.properties.AuthStage, authStage),
            getGenericEventPropertiesObject(InstrumentationService.properties.InitClientDuration, initDuration),
            getGenericEventPropertiesObject(InstrumentationService.properties.GetAccountDuration, accountDuration),
            getGenericEventPropertiesObject(InstrumentationService.properties.SilentTokenDuration, silentDuration),
            getGenericEventPropertiesObject(InstrumentationService.properties.PopupTokenDuration, popupDuration),
            getGenericEventPropertiesObject(InstrumentationService.properties.TeamsTokenDuration, teamsAuthDuration),
            getGenericEventPropertiesObject(InstrumentationService.properties.Duration, Date.now() - startInitTime),
            getGenericEventPropertiesObject(InstrumentationService.properties.ErrorMessage, previousErrorType ? `PreviousError code: ${previousErrorType.errorCode} message: ${previousErrorType.errorMessage} subError: ${previousErrorType.subError}` : "")
        ]);
    }

    private logAuthTokenCoverageEvent(isNaaAuth: boolean, isSuccess: boolean, startTime: number): void {
        if (this._isAuthTokenCoverageLogged) {
            return;
        }
        this._isAuthTokenCoverageLogged = true;
        InstrumentationService.logPerfEvent(InstrumentationService.perfScenarios.AuthStart, [
            getGenericEventPropertiesObject(InstrumentationService.properties.IsNestedAppAuth, isNaaAuth),
            getGenericEventPropertiesObject(InstrumentationService.properties.Duration, Date.now() - startTime),
            getGenericEventPropertiesObject(InstrumentationService.properties.IsSuccess, isSuccess)
        ]);
    }

    /**
     * Get the client id and resource uri based on the environment
     * When requesting shifts api token for Prod, use https://api.manage.staffhub.office.com/.default or https://api.manage.staffhub.office.com/<scope_name>
     * When requesting shifts api token for Dev, use api://c8d1eda9-813a-44d6-9a82-92eedbc102d9/.default or api://c8d1eda9-813a-44d6-9a82-92eedbc102d9/<scope_name>
     * When requesting graph api token, use https://graph.microsoft.com/.default or https://graph.microsoft.com/<scope_name>
     * When requesting teams spaces token, use https://api.spaces.skype.com/.default or https://api.spaces.skype.com/<scope_name>
     * @param {string} resource - mapped to resource uri and the uri will be used to fetch AAD token.
     * @returns {Object} clientId and resourceUri
     * */
    private getClientIdAndResourceUri(resource: string): { clientId: string, resourceUri: string } {
        const ecsEnvironmentFilter = AppSettings.getSetting("ECSEnvironmentFilter");
        const isProdEnvironment = !["Daily", "Development"].includes(ecsEnvironmentFilter);
        const clientId = isProdEnvironment ? PROD_CLIENT_ID : DEV_CLIENT_ID;
        const shiftsResourceUri = isProdEnvironment ? SHIFTS_RESOURCE_URI_PROD : `api://${clientId}`;
        let defaultResourceUri = '.default';
        switch (resource) {
            case TOKEN_RESOURCES.ShiftsService:
                defaultResourceUri = shiftsResourceUri;
                break;
            case TOKEN_RESOURCES.GraphServiceResourceUri:
                defaultResourceUri = GRAPH_RESOURCE_URI;
                break;
            case TOKEN_RESOURCES.TeamsSpaces:
                defaultResourceUri = TEAMS_SPACES_RESOURCE_URI;
        }
        const resourceUri = AppSettings.getSetting(resource) || defaultResourceUri;
        return { clientId, resourceUri };
    }

    /**
     * Initialize the nested public client application for MSAL
     * dev client id c8d1eda9-813a-44d6-9a82-92eedbc102d9
     * prod client id aa580612-c342-4ace-9055-8edee43ccb89
     * @param clientId - dev or prod client id depends on the environment. must match the client id in token request.
     * @returns {Object} promise.
     */
    private async initializePublicClient(clientId: string): Promise<msal.IPublicClientApplication> {
        try {
            const msalConfig = {
                auth: {
                    clientId: clientId,
                    authority: "https://login.microsoftonline.com/common"
                }
            };
            this._pca = await msal.createNestablePublicClientApplication(msalConfig);
        } catch (error) {
            trace.warn(`Error creating client app ${error}`);
            InstrumentationService.trackException(
                error, "NAA_failed to initialize NAA client app"
            );
        }

        return this._pca;
    }

    /**
     * Get active account for silent token request
     * @returns {Object} promise.
     */
    private async getActiveAccount(): Promise<msal.AccountInfo | null> {
        trace.info("Starting getActiveAccount");
        let activeAccount = null;
        const tenantId = UserStore().user?.tenantId;
        const userId = UserStore().user?.id;
        try {
            activeAccount = await this._pca.getActiveAccount();
        } catch (error) {
            trace.warn(`Error getting active account tenant: ${tenantId}, user: ${userId}. ${error}`);
            InstrumentationService.trackException(
                error, `NAA_failed to get active account`
            );
        }

        if (!activeAccount) {
            trace.info("No active account found, trying login popup");
            try {
                const accountFilter = {
                    tenantId: tenantId,
                    homeAccountId: userId
                };
                const accountWithFilter = this._pca.getAccount(accountFilter);
                if (accountWithFilter) {
                    activeAccount = accountWithFilter;
                    this._pca.setActiveAccount(activeAccount);
                }
            } catch (error) {
                trace.warn(`Error getting active account while trying login popup for tenant ${tenantId}, user: ${userId}. ${error}`);
                InstrumentationService.trackException(
                    error, `NAA_failed to get active account while trying login popup`
                );
            }
        }
        if (activeAccount) {
            trace.info("getActiveAccount completed");
        }

        return activeAccount;
    }

    /**
     * Checks if the error response is due to a CAE (Continuous Auth Evaluation) challenge and returns the claims
     * these claims will be sent to the Teams SDK to get a new token to retry the request
     * @param httpError - Axios error response from resource provider
     * @returns {string} - The claims if this was a CAE claims challenge error, undefined otherwise
     */
     public getClaimsIfCAEChallengeResponse(errorResponse: AxiosResponse): string {
        if (errorResponse?.status === 401 && errorResponse.headers[WWW_AUTHENTICATE_HEADER]) {
            return this.parseClaimsFromAuthenticateHeader(errorResponse.headers[WWW_AUTHENTICATE_HEADER]);
        }
        return undefined;
    }

    /**
     * Helper method to parse the claims challenge from the response header
     * @param wwwAuthenticateHeader - e.g. Bearer realm="", authorization_uri="https://login.microsoftonline.com/common/oauth2/authorize", error="insufficient_claims", claims="eyJhY2Nlc3NfdG9rZW4iOnsiYWNycyI6eyJlc3NlbnRpYWwiOnRydWUsInZhbHVlIjoiYzEifX19"
     */
     public parseClaimsFromAuthenticateHeader(wwwAuthenticateHeader: string): string {
        const keyValMap: Map<string, string> = this.parseAuthenticateHeader(wwwAuthenticateHeader);
        return keyValMap?.get("claims");
    }

    /**
     * Helper method to parse the www-authenticate header into its components
     * @param wwwAuthenticateHeader - e.g. Bearer realm="", authorization_uri="https://login.microsoftonline.com/common/oauth2/authorize", error="insufficient_claims", claims="eyJhY2Nlc3NfdG9rZW4iOnsiYWNycyI6eyJlc3NlbnRpYWwiOnRydWUsInZhbHVlIjoiYzEifX19"
     */
     public parseAuthenticateHeader(wwwAuthenticateHeader: string): Map<string, string> {

        // remove the Bearer token and split on ','
        const components = wwwAuthenticateHeader?.replace(BEARERKEY, "").split(",");

        const keyValMap: Map<string, string> = new Map<string, string>();
        if (components?.length) {
            for (let component of components) {
                // e.g. claims="eyJhY2Nlc3NfdG9rZW4iOnsiYWNycyI6eyJlc3NlbnRpYWwiOnRydWUsInZhbHVlIjoiYzEifX19"
                const keyVals = component.split("=");
                if (keyVals?.length === 2) {
                    const key = keyVals[0] ? keyVals[0].trim() : undefined;
                    const value = keyVals[1] ? keyVals[1].replace(/^"/, "").replace(/"$/, "") : undefined;
                    if (key && value !== undefined) {
                        keyValMap.set(key, value);
                    }
                }
            }
        }

        return keyValMap;
    }
}

const service = new AuthService();
export default service as AuthService;